import {computed, observable, makeObservable} from 'mobx';

import events from 'events.js';

import Store from 'core/stores/Store.js';
import {sessionStorageGetItem, sessionStorageSetItem} from 'core/utils.js';
import {
    TRUE_STR,
    BP,
    BP_FHIR,
    MD,
    REFERRALS,
    SITE,
    HCP,
    GP,
    ZM,
    LB,
} from 'core/constants.js';

export default class ParamStore extends Store {
    constructor(rootStore) {
        super();
        this.rootStore = rootStore;
        makeObservable(this, {
            client: observable,
            isClient: observable,
            isHfWidget: observable,
            isReferrals: observable,
            isStaff: observable,
            skin: observable,
            userType: observable,
            mh1BookingsUrl: observable,
            mh1AvailabilityUrl: observable,
            apiVersion: observable,
            hcfForceV1Directory: observable,

            hasSkin: computed,
            showParam: computed,
            isProUser: computed,
        });

        this.CLIENTS = [BP, BP_FHIR, MD, ZM, LB];

        this.setCameFromReferrals();
        this.setSessionVariables();
        this.handleLinkBehaviour();
    }

    client = '';
    isClient = false;

    skin = '';

    isHfWidget = false;
    isReferrals = false;
    isStaff = false;
    userType = '';
    mh1BookingsUrl = '';
    mh1AvailabilityUrl = '';
    apiVersion = 1;
    hcfForceV1Directory = false;

    async handleLinkBehaviour() {
        var self = this;

        events.ready().then(function () {
            if (self.client !== LB) return;

            document.addEventListener('click', function (e) {
            e = e || window.event;
            var target = e.target || e.srcElement;

            var anchor = null;
            while (target) {
                if (target.tagName && target.tagName.toLowerCase() === 'a') {
                anchor = target;
                break;
                }
                target = target.parentNode;
            }

            if (!anchor) return;

            var href = anchor.getAttribute('href');
            if (!href) return;

            if (e.defaultPrevented) return;

            if (
                href.indexOf('#') === 0 ||
                href.indexOf('tel:') === 0 ||
                href.indexOf('mailto:') === 0 ||
                href.indexOf('javascript:') === 0 ||
                anchor.getAttribute('download') !== null
            ) {
                return;
            }

            anchor.setAttribute('target', '_self');

            if (e.preventDefault) {
                e.preventDefault();
            } else {
                e.returnValue = false;
            }

            window.location.href = href;
            }, true);
        });
    }

    async setCameFromReferrals() {
        await events.ready();
        if (sessionStorageGetItem('cameFromReferrals') === null) {
            if (
                location.pathname === '/referrals/' ||
                location.pathname === '/redesign/referrals/'
            ) {
                sessionStorageSetItem('cameFromReferrals', true);
            } else {
                sessionStorageSetItem('cameFromReferrals', false);
            }
        }
    }

    async setSessionVariables() {
        await events.ready();
        const {
            client,
            integratedClient,
            isHfWidget,
            isReferrals,
            isStaff,
            skin,
            userType,
            mh1BookingsUrl,
            mh1AvailabilityUrl,
            apiVersion,
            hcfForceV1Directory,
        } = document.body.dataset;

        const storeParams = {
            isClient: integratedClient === TRUE_STR,
            isReferrals: isReferrals === TRUE_STR,
            isStaff: isStaff === TRUE_STR,
            isHfWidget: isHfWidget === TRUE_STR,
            mh1BookingsUrl: mh1BookingsUrl,
            mh1AvailabilityUrl: mh1AvailabilityUrl,
            hcfForceV1Directory: hcfForceV1Directory === TRUE_STR,
        };
        if (this.CLIENTS.includes(client)) {
            storeParams['client'] = client;
            if (client == LB) {
                const {
                    bpPracticeUserId,
                    bpSiteId,
                    bpUserId,
                    lbPracticeId,
                    lbUserId,
                    postcode,
                } = document.body.dataset;
                storeParams['bpPracticeUserId'] = bpPracticeUserId;
                storeParams['bpSiteId'] = bpSiteId;
                storeParams['bpUserId'] = bpUserId;
                storeParams['lbPracticeId'] = lbPracticeId;
                storeParams['lbUserId'] = lbUserId;
                storeParams['postcode'] = postcode;
            }
        }
        if (skin) {
            storeParams['skin'] = skin;
        }
        if (userType) {
            storeParams['userType'] = userType;
        }
        if (apiVersion > 1) {
            storeParams['apiVersion'] = apiVersion;
        }

        this.updateStore({...storeParams});
    }

    get dataSource() {
        const selectedHF = this.rootStore?.healthFundStore?.healthFund;
        let source = 'healthshare';

        if (this.isHfWidget) {
            source = `${source}-fap-${selectedHF?.name}`;
        } else if (selectedHF?.id) {
            source = `${source}-filter-${selectedHF?.name}`;
        }

        return {
            source,
        };
    }

    get hasSkin() {
        return this.skin && !this.isClient;
    }

    get isProUser() {
        return this.userType && this.userType === HCP;
    }

    get isLoggedInGP() {
        return this.userType && this.userType === GP;
    }

    get showParam() {
        const inReferralsPage =
            window.location.pathname.includes('/referrals/');
        // To allow a referrals user to navigate the normal flow of the website
        // and see non-referrals specialties
        // Session storage variable are strings even when they're saved as bools
        const fromReferrals =
            sessionStorageGetItem('cameFromReferrals') === 'true';
        const isReferralsUser = this.isReferrals;
        if (
            this.isClient ||
            inReferralsPage ||
            (fromReferrals && isReferralsUser)
        ) {
            return REFERRALS;
        }
        return SITE;
    }
}
